import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Avatar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TimelineIcon from '@mui/icons-material/Timeline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { supabase } from '../../services/supabase';

interface PlayerStats {
  player: {
    id: string;
    nickname: string;
    full_name: string;
    profile_picture_url?: string;
  };
  totalPoints: number;
  wins: number;
  losses: number;
  games: number;
  totalBonusPoints: number;
  killedFirstCount: number;
  killedFirstAndLostCount: number;
  winningMoveBonus: number;
}

interface PointsProgressGraphProps {
  topPlayers: PlayerStats[];
  leagueId: string;
  availableSessions: number[];
  equalizeGames: boolean;
}

interface SessionDataPoint {
  session: number;
  [playerNickname: string]: number | string;
}

// Colors for top 5 players - gold, silver, bronze for top 3
const PLAYER_COLORS = [
  '#FFD700', // Gold - 1st
  '#C0C0C0', // Silver - 2nd
  '#CD7F32', // Bronze - 3rd
  '#4FC3F7', // Light Blue - 4th
  '#81C784', // Green - 5th
];

const PointsProgressGraph: React.FC<PointsProgressGraphProps> = ({
  topPlayers,
  leagueId,
  availableSessions,
  equalizeGames
}) => {
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [sessionData, setSessionData] = useState<SessionDataPoint[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for triggering animation when scrolled into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
            setAnimationKey(prev => prev + 1);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [isVisible]);

  // Fetch session-by-session data when component mounts or equalizeGames changes
  useEffect(() => {
    console.log('PointsProgressGraph effect:', { expanded, sessionDataLength: sessionData.length, topPlayersLength: topPlayers.length, equalizeGames });
    if (topPlayers.length > 0) {
      console.log('Fetching session data...');
      fetchSessionData();
    }
  }, [topPlayers, equalizeGames]);

  /**
   * Calculate points using the same formula as LeagueStats
   */
  const calculateTotalPoints = (
    wins: number,
    losses: number,
    bonusPoints: number,
    totalGames: number,
    killedFirstCount: number = 0,
    killedFirstAndLostCount: number = 0,
    winningMoveBonus: number = 0
  ): number => {
    let points = 0;

    let killedFirstModifier = 0.2;
    if (killedFirstCount > 3) {
      killedFirstModifier = 0.4;
    } else if (killedFirstCount > 6) {
      killedFirstModifier = 0.5;
    }

    points += killedFirstAndLostCount * killedFirstModifier;
    points += wins * 0.6;
    points -= losses * 0.4;
    points += bonusPoints;
    points += winningMoveBonus;

    if (totalGames >= 20) {
      points += 2;
    }

    points = points * 10 + 50;
    return Number(points.toFixed(1));
  };

  /**
   * Calculate equalized points using the same formula as LeagueStats
   */
  const calculateEqualizedTotalPoints = (
    wins: number,
    losses: number,
    bonusPoints: number,
    totalGames: number,
    killedFirstCount: number = 0,
    killedFirstAndLostCount: number = 0,
    winningMoveBonus: number = 0,
    maxGames: number
  ): number => {
    // If player has played max games or more, return normal calculation
    if (totalGames >= maxGames) {
      return calculateTotalPoints(wins, losses, bonusPoints, totalGames, killedFirstCount, killedFirstAndLostCount, winningMoveBonus);
    }

    // Require at least 10 games to qualify for equalization bonus
    if (totalGames < 10) {
      return calculateTotalPoints(wins, losses, bonusPoints, totalGames, killedFirstCount, killedFirstAndLostCount, winningMoveBonus);
    }

    // Calculate normal score first
    let points = calculateTotalPoints(wins, losses, bonusPoints, totalGames, killedFirstCount, killedFirstAndLostCount, winningMoveBonus);

    // Calculate the equalization adjustment
    const equalizablePoints = bonusPoints + winningMoveBonus;
    const averageEqualizablePerGame = totalGames > 0 ? equalizablePoints / totalGames : 0;
    const missingGames = maxGames - totalGames;
    let equalizationBonus = (averageEqualizablePerGame * missingGames) + (0.1 * missingGames);
    if (totalGames < 20) {
      equalizationBonus += 2;
    }

    // Apply the equalization bonus with the same scaling as the main formula (75% effectiveness)
    const finalPoints = points + (equalizationBonus * 10 * 0.75);

    return Number(finalPoints.toFixed(1));
  };

  const fetchSessionData = async () => {
    console.log('fetchSessionData called for league:', leagueId);
    setLoading(true);
    try {
      // Get all games sorted by session
      const { data: gamesData, error: gamesError } = await supabase
        .from('games')
        .select('id, session_num')
        .eq('league_id', leagueId)
        .order('session_num', { ascending: true });

      if (gamesError) throw gamesError;
      if (!gamesData || gamesData.length === 0) {
        setSessionData([]);
        return;
      }

      const gameIds = gamesData.map(g => g.id);

      // Get top 5 player IDs
      const topPlayerIds = topPlayers.slice(0, 5).map(p => p.player.id);
      const playerNicknameMap: { [id: string]: string } = {};
      topPlayers.slice(0, 5).forEach(p => {
        playerNicknameMap[p.player.id] = p.player.nickname;
      });

      // Get all game results for these games (need all for winning move calculation)
      const { data: resultsData, error: resultsError } = await supabase
        .from('game_results')
        .select('game_id, player_id, is_win, bonus_points, is_killed_first, winning_move, role, player_num')
        .in('game_id', gameIds);

      if (resultsError) throw resultsError;
      if (!resultsData) {
        setSessionData([]);
        return;
      }

      // Create a map of game_id to session_num
      const gameToSession: { [gameId: string]: number } = {};
      gamesData.forEach(game => {
        gameToSession[game.id] = game.session_num;
      });

      // Group results by game for winning move calculation
      const resultsByGame: { [gameId: string]: any[] } = {};
      resultsData.forEach(result => {
        if (!resultsByGame[result.game_id]) {
          resultsByGame[result.game_id] = [];
        }
        resultsByGame[result.game_id].push(result);
      });

      // Get unique sessions sorted
      const uniqueSessions = Array.from(new Set(gamesData.map(g => g.session_num))).sort((a, b) => a - b);

      // Track cumulative stats per player (top 5 only, with full stats)
      const playerCumulativeStats: { [playerId: string]: {
        wins: number;
        losses: number;
        bonusPoints: number;
        killedFirstCount: number;
        killedFirstAndLostCount: number;
        winningMoveBonus: number;
        totalGames: number;
      }} = {};

      // Track games count for ALL players (for maxGames calculation)
      const allPlayerGameCounts: { [playerId: string]: number } = {};

      // Initialize stats for each top player
      topPlayerIds.forEach(playerId => {
        playerCumulativeStats[playerId] = {
          wins: 0,
          losses: 0,
          bonusPoints: 0,
          killedFirstCount: 0,
          killedFirstAndLostCount: 0,
          winningMoveBonus: 0,
          totalGames: 0
        };
      });

      // Calculate cumulative points for each session
      const sessionDataPoints: SessionDataPoint[] = [];

      uniqueSessions.forEach(sessionNum => {
        // Get all game IDs for this session
        const sessionGameIds = gamesData
          .filter(g => g.session_num === sessionNum)
          .map(g => g.id);

        sessionGameIds.forEach(gameId => {
          const gameResults = resultsByGame[gameId] || [];
          const mafiaPlayers = gameResults.filter((r: any) => r.role === 'MAFIA' || r.role === 'DON');

          gameResults.forEach((result: any) => {
            // Track games for ALL players (for maxGames calculation)
            if (!allPlayerGameCounts[result.player_id]) {
              allPlayerGameCounts[result.player_id] = 0;
            }
            allPlayerGameCounts[result.player_id] += 1;

            // Only track detailed stats for top 5
            if (!topPlayerIds.includes(result.player_id)) return;

            const stats = playerCumulativeStats[result.player_id];
            stats.totalGames += 1;

            if (result.is_win) {
              stats.wins += 1;
            } else {
              stats.losses += 1;
            }

            stats.bonusPoints += Number(result.bonus_points || 0);

            if (result.is_killed_first) {
              stats.killedFirstCount += 1;
              if (!result.is_win) {
                stats.killedFirstAndLostCount += 1;
              }

              // Calculate winning move bonus
              if ((result.role === 'CITIZEN' || result.role === 'SHERIFF') && result.winning_move) {
                try {
                  let guessedMafiaNumbers: number[] = [];
                  if (result.winning_move.includes(',')) {
                    guessedMafiaNumbers = result.winning_move.split(',').map((n: string) => parseInt(n.trim(), 10));
                  } else if (result.winning_move.includes(' ')) {
                    guessedMafiaNumbers = result.winning_move.split(' ').map((n: string) => parseInt(n.trim(), 10));
                  } else {
                    guessedMafiaNumbers = result.winning_move.match(/\d+/g)?.map((n: string) => parseInt(n, 10)) || [];
                  }

                  const correctGuesses = guessedMafiaNumbers.filter((playerNum: number) =>
                    mafiaPlayers.some((mp: any) => mp.player_num === playerNum)
                  ).length;

                  if (correctGuesses === 2) {
                    stats.winningMoveBonus += 0.5;
                  } else if (correctGuesses === 3) {
                    stats.winningMoveBonus += 0.8;
                  }
                } catch (e) {
                  console.error('Error processing winning move:', e);
                }
              }
            }
          });
        });

        // Calculate points for each player at this session
        const dataPoint: SessionDataPoint = { session: sessionNum };

        // Find max games across ALL players at this point for equalization (same as table)
        const allGameCounts = Object.values(allPlayerGameCounts);
        const maxGamesAtSession = allGameCounts.length > 0 ? Math.max(...allGameCounts) : 0;

        topPlayerIds.forEach(playerId => {
          const stats = playerCumulativeStats[playerId];
          const nickname = playerNicknameMap[playerId];

          if (stats.totalGames > 0) {
            let points: number;

            if (equalizeGames) {
              // Use equalized calculation
              points = calculateEqualizedTotalPoints(
                stats.wins,
                stats.losses,
                stats.bonusPoints,
                stats.totalGames,
                stats.killedFirstCount,
                stats.killedFirstAndLostCount,
                stats.winningMoveBonus,
                maxGamesAtSession
              );
            } else {
              // Use normal calculation
              points = calculateTotalPoints(
                stats.wins,
                stats.losses,
                stats.bonusPoints,
                stats.totalGames,
                stats.killedFirstCount,
                stats.killedFirstAndLostCount,
                stats.winningMoveBonus
              );
            }

            dataPoint[nickname] = points;
          }
        });

        sessionDataPoints.push(dataPoint);
      });

      console.log('Session data points:', sessionDataPoints);
      setSessionData(sessionDataPoints);
    } catch (error) {
      console.error('Error fetching session data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    console.log('Toggle clicked, current expanded:', expanded);
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (newExpanded) {
      // Reset animation when opening
      setIsVisible(false);
      setTimeout(() => setIsVisible(true), 100);
    }
  };

  // Get player data for the chart (top 5 only)
  const top5Players = topPlayers.slice(0, 5);
  const playerNicknames = top5Players.map(p => p.player.nickname);

  // Create a map of nickname to player for legend
  const nicknameToPlayer: { [nickname: string]: typeof top5Players[0]['player'] } = {};
  top5Players.forEach(p => {
    nicknameToPlayer[p.player.nickname] = p.player;
  });

  // Custom legend with profile photos
  const renderLegend = (props: any) => {
    const { payload } = props;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, pt: 2 }}>
        {payload?.map((entry: any, index: number) => {
          // Find player by nickname (entry.value is the dataKey which is the nickname)
          const player = nicknameToPlayer[entry.value];
          return (
            <Box key={entry.value} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Avatar
                src={player?.profile_picture_url || undefined}
                sx={{
                  width: 24,
                  height: 24,
                  border: `2px solid ${entry.color}`,
                  fontSize: '0.7rem'
                }}
              >
                {player?.nickname?.substring(0, 2).toUpperCase()}
              </Avatar>
              <Typography variant="body2" sx={{ color: entry.color, fontWeight: index < 3 ? 'bold' : 'normal' }}>
                {entry.value}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  console.log('PointsProgressGraph render:', { expanded, loading, sessionDataLength: sessionData.length, playerNicknames });

  return (
    <Paper
      ref={containerRef}
      sx={{
        mb: 4,
        borderRadius: 2,
        overflow: 'hidden',
        background: 'linear-gradient(145deg, rgba(92, 107, 192, 0.1), rgba(92, 107, 192, 0.2))',
        border: '1px solid rgba(92, 107, 192, 0.3)'
      }}
    >
      <Accordion
        expanded={expanded}
        disableGutters
        sx={{
          bgcolor: 'transparent',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          pointerEvents: 'none'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="points-progress-content"
          id="points-progress-header"
          onClick={handleToggle}
          sx={{
            cursor: 'pointer',
            pointerEvents: 'auto',
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              gap: 2
            }
          }}
        >
          <TimelineIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Top 5 over time
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            (Click to {expanded ? 'collapse' : 'expand'})
          </Typography>
        </AccordionSummary>

        <AccordionDetails id="points-progress-content" sx={{ pointerEvents: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : sessionData.length > 0 ? (
            <Box sx={{ width: '100%', height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  key={animationKey}
                  data={sessionData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis
                    dataKey="session"
                    label={{ value: 'Session', position: 'bottom', offset: 0 }}
                    tick={{ fill: '#fff' }}
                  />
                  <YAxis
                    label={{ value: 'Points', angle: -90, position: 'insideLeft' }}
                    tick={{ fill: '#fff' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(30, 30, 30, 0.95)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: 8
                    }}
                    labelFormatter={(label) => `Session ${label}`}
                  />
                  <Legend content={renderLegend} />
                  {playerNicknames.map((nickname, index) => (
                    <Line
                      key={nickname}
                      type="monotone"
                      dataKey={nickname}
                      stroke={PLAYER_COLORS[index]}
                      strokeWidth={index < 3 ? 3 : 2}
                      dot={{ r: index < 3 ? 5 : 3, fill: PLAYER_COLORS[index] }}
                      activeDot={{ r: 8 }}
                      isAnimationActive={isVisible}
                      animationDuration={2000}
                      animationEasing="ease-in-out"
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No session data available to display
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default PointsProgressGraph;
